import { useEffect } from "react";
import { V2Shell } from "@/v2/components/V2Shell";
import { Footer } from "@/v2/components/Footer";

export interface LegalParagraphBlock {
  type: "p";
  text: string;
}

export interface LegalListBlock {
  type: "list";
  items: string[];
}

export type LegalBlock = LegalParagraphBlock | LegalListBlock;

export interface LegalSection {
  title: string;
  blocks: LegalBlock[];
}

// The content is migrated verbatim from the legacy legal pages. Some sections
// (e.g. the governing-law and DMCA disclaimers) are intentionally ALL CAPS in
// the source document, so we render every block as-is. An earlier heuristic
// "normalize to sentence case" pass was removed: it silently lowercased proper
// nouns ("Commonwealth of Massachusetts", "Digital Millennium Copyright Act")
// and changed the wording it claimed to preserve.

function LegalSectionView({
  title,
  blocks,
  isFirst,
}: {
  title: string;
  blocks: LegalBlock[];
  isFirst: boolean;
}) {
  return (
    <section
      className={isFirst ? undefined : "mt-4 border-t border-slate-200 pt-2"}
    >
      {title ? (
        // Inline color/fontSize/fontWeight: v1's unlayered `h2 { color; font-size;
        // font-weight }` (src/index.css) outranks Tailwind's utilities layer, so
        // text-* utilities on this heading are silently ignored. Inline styles
        // always win regardless of layers, so these three properties stay
        // inline; everything else (margin) can use a plain Tailwind utility.
        <h2
          className="mb-1"
          style={{
            color: "var(--v2-foreground)",
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          {title}
        </h2>
      ) : null}
      <div className="flex flex-col gap-4">
        {blocks.map((block, index) =>
          block.type === "list" ? (
            <ul key={index} className="flex flex-col gap-2 pl-1">
              {block.items.map((item, itemIndex) => (
                <li
                  key={itemIndex}
                  className="flex gap-2 text-[15px] text-slate-600 leading-[1.8]"
                >
                  <span className="select-none text-slate-400">&bull;</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div
              key={index}
              className="text-[15px] text-slate-600 leading-[1.8]"
            >
              {block.text}
            </div>
          ),
        )}
      </div>
    </section>
  );
}

interface LegalDocumentPageProps {
  title: string;
  subtitle: string;
  sections: LegalSection[];
}

/**
 * Shared v2 layout for long-form legal pages (Privacy Policy, Terms and
 * Conditions, ...). Relies on the app's existing global v2 nav
 * (PublicRoute/MainContainer) rather than rendering its own header.
 */
export function LegalDocumentPage({ title, subtitle, sections }: LegalDocumentPageProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <V2Shell>
      <div className="bg-slate-50/50">
        <div className="mx-auto w-full v2-legal-gutter py-4">
          {/* Inline color/fontSize/fontWeight: same v1 `h1` unlayered override as
              the section heading above; margin stays a Tailwind utility. */}
          <h1
            className="mb-2"
            style={{
              color: "var(--v2-foreground)",
              fontSize: "28px",
              fontWeight: 700,
              lineHeight: "1.2",
            }}
          >
            {title}
          </h1>
          <div className="mb-4 text-[15px] text-muted-foreground">{subtitle}</div>
          <div className="rounded-2xl border border-slate-200/60 bg-card p-3 shadow-sm sm:p-4">
            {sections.map((section, index) => (
              <LegalSectionView
                key={index}
                title={section.title}
                blocks={section.blocks}
                isFirst={index === 0}
              />
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </V2Shell>
  );
}
